/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ResponseWrapper} from '../../model/responseWrapper';

const TIMEOUT_CHECK_INTERVAL = 20 * 60 * 1000; // how often(ms) to check if user is active (Session Timeout - 20 Minutes)
const DEBOUNCE_INTERVAL = 2 * 60 * 1000; // wait before backend call to be made ( 2 Minutes)
const COUNTDOWN_TICK_INTERVAL = 1000; // decrement counter every second ( 1s = 1000 ms) for the expiry countdown
const DEFAULT_MESSAGE = `Your session is about to expire!`;
const HEARTBEAT_URL = '/esciUI/heartbeat';

@Component({
    selector: 'user-session-timeout',
    templateUrl: './user-session-timeout.component.html',
    styleUrls: ['./user-session-timeout.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class UserSessionTimeoutComponent implements OnInit {

  /** Signals avoid NG0100 when the countdown interval fires during dev-mode CD checks. */
  readonly isUserActive = signal(true);
  readonly counter = signal(3 * 60); // 3 minutes
  readonly message = signal(DEFAULT_MESSAGE);
  /** Mirrors whether the expiry countdown interval is running (template must not read the raw timer handle). */
  readonly countdownActive = signal(false);

  private sessionExpiryCountDownInterval: ReturnType<typeof setInterval> | undefined;
  private userActivityCheckInterval: ReturnType<typeof setInterval> | undefined;
  userActivityDetected = false;

  constructor(private httpClient: HttpClient) {
  }

  ngOnInit(): void {
    // Adds Global event listeners to the <body> of the application
    document.body.addEventListener('click', () => this.updateUserActivity());
    document.body.addEventListener('keyup', () => this.updateUserActivity());

    this.initUserActivityCheckInterval();
    this.setHeartBeatInterval();
  }

  initUserActivityCheckInterval() {
    clearInterval(this.userActivityCheckInterval);
    this.isUserActive.set(true);
    this.userActivityDetected = false;
    this.userActivityCheckInterval = setInterval(() => {
      this.showSessionExpiryDialog();
    }, TIMEOUT_CHECK_INTERVAL);
  }

  /**
   * Shows Session Expiry 2 minutes Countdown Dialog
   */
  private showSessionExpiryDialog() {
    // If the user is still active, do nothing
    if (this.userActivityDetected) {
      return;
    }
    // User is inactive, show the countdown
    clearInterval(this.userActivityCheckInterval);
    this.message.set(DEFAULT_MESSAGE);
    this.isUserActive.set(false);
    this.sessionExpiryCountDownInterval = this.startSessionExpiryCountdown();
    this.countdownActive.set(true);
  }

  /**
   * Starts the countdown timer
   * @returns {NodeJS.Timeout}
   */
  private startSessionExpiryCountdown() {

    return setInterval(() => {
      this.counter.update((c) => c - 1);
      if (this.counter() === 0) {
        this.clearSessionExpiryCountdown();
        this.message.set('Session has been expired, Please reload the application.');
        this.counter.set(180);
      }
    }, COUNTDOWN_TICK_INTERVAL);
  }

  /**
   * Clears the countdown Timer
   */
  private clearSessionExpiryCountdown() {
    clearInterval(this.sessionExpiryCountDownInterval);
    this.sessionExpiryCountDownInterval = undefined;
    this.countdownActive.set(false);
    this.counter.set(180);
  }

  /**
   * Process the heart beat call and wait for {DEBOUNCE_INTERVAL} ms before calling the backend
   */
  private setHeartBeatInterval() {
    setInterval(() => {
      if (this.userActivityDetected) {
        this.sendHeartBeat().subscribe((response: ResponseWrapper) => {
          response ? this.initUserActivityCheckInterval() :
            console.error('UNABLE TO GET HEART BEAT', response);
        }, (err: HttpErrorResponse) => {
          console.error('UNABLE TO GET HEART BEAT', err);
        });
      }
    }, DEBOUNCE_INTERVAL);
  }

  private sendHeartBeat(): Observable<any> {
    return this.httpClient.get<ResponseWrapper>(environment.apiUrl + HEARTBEAT_URL);
  }

  private updateUserActivity() { // when user clicks / keyup this method is called
    if (!this.sessionExpiryCountDownInterval) {
      this.clearSessionExpiryCountdown();
      this.userActivityDetected = true;
    }
  }

  // *****************TEMPLATE METHODS*******************//
  /**
   * Extends the session
   */
  extendUserSession() {

    this.clearSessionExpiryCountdown();
    this.sendHeartBeat().subscribe((res: ResponseWrapper) => {

      res.success ? this.initUserActivityCheckInterval() :
        console.error('UNABLE TO GET HEARTBEAT', res);
    }, (err: HttpErrorResponse) => {

      console.error(err);
      this.message.set('Unable to extend the session, Please reload the application');
    });
  }

  /**
   * Reload Application when session expires
   */
  onReloadApplication() {
    window.location.reload();
  }

}
